import React, { useState } from 'react';
import { FileText, Upload, CheckCircle, Star, Download, Eye } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { screenCandidates } from '../../../services/openaiService';

const SmartCandidateScreening = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [screeningComplete, setScreeningComplete] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [screeningStats, setScreeningStats] = useState(null);
  const [error, setError] = useState(null);

  const mockCandidates = [
  {
    id: 1,
    name: 'Priya Sharma',
    email: 'priya.sharma@email.com',
    phone: '+91 98765 43210',
    position: 'Frontend Developer',
    overallScore: 94,
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1565e8423-1763295004978.png",
    avatarAlt: 'Professional woman with long dark hair in business attire',
    resumeUrl: '#',
    appliedDate: '2026-02-01',
    scores: {
      skillsMatch: 96,
      experience: 92,
      education: 95,
      projects: 93
    },
    keyHighlights: [
    '3+ years React.js experience',
    'Strong portfolio with 12+ projects',
    'B.Tech in Computer Science - 8.9 CGPA',
    'Previous internship at top tech company'],

    skills: ['React', 'JavaScript', 'TypeScript', 'Tailwind CSS', 'Git', 'REST APIs'],
    education: 'B.Tech Computer Science, IIT Delhi',
    experience: '3 years',
    status: 'Highly Recommended'
  },
  {
    id: 2,
    name: 'Rahul Verma',
    email: 'rahul.verma@email.com',
    phone: '+91 98765 43211',
    position: 'Full Stack Developer',
    overallScore: 89,
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_115f04544-1763294281729.png",
    avatarAlt: 'Professional man with short hair wearing casual business attire',
    resumeUrl: '#',
    appliedDate: '2026-02-02',
    scores: {
      skillsMatch: 91,
      experience: 88,
      education: 87,
      projects: 90
    },
    keyHighlights: [
    '2.5 years full-stack development',
    'Node.js and React expertise',
    'M.Tech from NIT Trichy',
    'Led team of 3 developers'],

    skills: ['React', 'Node.js', 'MongoDB', 'Express', 'AWS', 'Docker'],
    education: 'M.Tech Computer Science, NIT Trichy',
    experience: '2.5 years',
    status: 'Recommended'
  },
  {
    id: 3,
    name: 'Ananya Patel',
    email: 'ananya.patel@email.com',
    phone: '+91 98765 43212',
    position: 'UI/UX Designer',
    overallScore: 91,
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1e9cf08bc-1763300977993.png",
    avatarAlt: 'Young professional woman with friendly smile in modern office setting',
    resumeUrl: '#',
    appliedDate: '2026-02-01',
    scores: {
      skillsMatch: 93,
      experience: 89,
      education: 90,
      projects: 92
    },
    keyHighlights: [
    'Expert in Figma and Adobe XD',
    '50+ design projects completed',
    'Design degree from NIFT',
    'Won 2 design competitions'],

    skills: ['Figma', 'Adobe XD', 'Sketch', 'Prototyping', 'User Research', 'Wireframing'],
    education: 'B.Des, NIFT Mumbai',
    experience: '2 years',
    status: 'Highly Recommended'
  },
  {
    id: 4,
    name: 'Arjun Singh',
    email: 'arjun.singh@email.com',
    phone: '+91 98765 43213',
    position: 'Data Analyst',
    overallScore: 85,
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_108eabec6-1763294868963.png",
    avatarAlt: 'Professional man with beard in business casual attire',
    resumeUrl: '#',
    appliedDate: '2026-02-03',
    scores: {
      skillsMatch: 87,
      experience: 83,
      education: 86,
      projects: 84
    },
    keyHighlights: [
    'Python and SQL proficiency',
    'Data visualization expert',
    'MBA from IIM Bangalore',
    'Published research paper'],

    skills: ['Python', 'SQL', 'Tableau', 'Power BI', 'Excel', 'Statistics'],
    education: 'MBA, IIM Bangalore',
    experience: '1.5 years',
    status: 'Recommended'
  }];


  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 80) return 'text-blue-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-gray-600';
  };

  const getScoreBg = (score) => {
    if (score >= 90) return 'bg-green-50';
    if (score >= 80) return 'bg-blue-50';
    if (score >= 70) return 'bg-yellow-50';
    return 'bg-gray-50';
  };

  const getStatusBadge = (status) => {
    if (status === 'Highly Recommended') {
      return 'bg-green-100 text-green-700 border-green-200';
    }
    return 'bg-blue-100 text-blue-700 border-blue-200';
  };

  const handleFileUpload = async () => {
    try {
      setIsProcessing(true);
      setError(null);

      // Job requirements (in real app, this would come from actual job posting)
      const jobRequirements = {
        position: 'Frontend Developer',
        requiredSkills: ['React', 'JavaScript', 'TypeScript', 'CSS'],
        experienceLevel: '2-4 years',
        education: 'B.Tech/B.E. in Computer Science or related field',
        preferredQualifications: ['Portfolio of projects', 'Team collaboration experience']
      };

      // Simplified candidate data for AI analysis
      const candidateData = mockCandidates?.map((c) => ({
        id: c?.id,
        name: c?.name,
        position: c?.position,
        skills: c?.skills,
        education: c?.education,
        experience: c?.experience,
        resumeSummary: `${c?.experience} experience in ${c?.position}. Skills: ${c?.skills?.join(', ')}. Education: ${c?.education}.`
      }));

      // Call OpenAI API for real AI screening
      const aiResults = await screenCandidates(candidateData, jobRequirements);

      // Merge AI results with full candidate data
      const enhancedCandidates = aiResults?.rankedCandidates?.map((aiCandidate) => {
        const fullCandidate = mockCandidates?.find((c) => c?.id === aiCandidate?.candidateId);
        return {
          ...fullCandidate,
          overallScore: aiCandidate?.overallScore,
          scores: aiCandidate?.scores,
          keyHighlights: aiCandidate?.keyHighlights,
          status: aiCandidate?.status
        };
      })?.sort((a, b) => b?.overallScore - a?.overallScore);

      setScreeningStats({
        totalScreened: aiResults?.totalScreened,
        highlyQualified: aiResults?.highlyQualified,
        averageScore: aiResults?.averageScore,
        processingTime: aiResults?.processingTime
      });
      setCandidates(enhancedCandidates);
      setScreeningComplete(true);
    } catch (err) {
      setError(err?.message);
      // Fallback to mock data on error
      setCandidates(mockCandidates);
      setScreeningStats({
        totalScreened: 47,
        highlyQualified: 18,
        averageScore: 82,
        processingTime: '1.8s'
      });
      setScreeningComplete(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const displayCandidates = candidates?.length > 0 ? candidates : mockCandidates;
  const displayStats = screeningStats || {
    totalScreened: 47,
    highlyQualified: 18,
    averageScore: 82,
    processingTime: '1.8s'
  };

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Smart Resume Screening</h3>
            <p className="text-sm text-gray-600">AI-powered candidate evaluation and ranking</p>
          </div>
        </div>

        <div className="bg-white rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h4 className="text-lg font-semibold text-gray-900 mb-2">Upload Resumes for Screening</h4>
          <p className="text-gray-600 mb-4">Support for PDF, DOC, DOCX formats • Max 10MB per file</p>
          <Button onClick={handleFileUpload} disabled={isProcessing}>
            {isProcessing ? 'Processing...' : 'Upload Resumes'}
          </Button>
        </div>
      </div>
      {/* Screening Stats */}
      {screeningComplete &&
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-sm text-gray-600 mb-1">Total Screened</p>
            <p className="text-2xl font-bold text-gray-900">{displayStats?.totalScreened}</p>
            <p className="text-xs text-green-600 mt-1">+12 today</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-sm text-gray-600 mb-1">Highly Qualified</p>
            <p className="text-2xl font-bold text-green-600">{displayStats?.highlyQualified}</p>
            <p className="text-xs text-gray-600 mt-1">{Math.round(displayStats?.highlyQualified / displayStats?.totalScreened * 100)}% of total</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-sm text-gray-600 mb-1">Avg Score</p>
            <p className="text-2xl font-bold text-blue-600">{displayStats?.averageScore}</p>
            <p className="text-xs text-gray-600 mt-1">Out of 100</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-sm text-gray-600 mb-1">Processing Time</p>
            <p className="text-2xl font-bold text-purple-600">{displayStats?.processingTime}</p>
            <p className="text-xs text-gray-600 mt-1">Per resume</p>
          </div>
        </div>
      }
      {/* Error Message */}
      {error &&
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            <strong>Note:</strong> {error} Showing sample data.
          </p>
        </div>
      }
      {/* Candidate Rankings */}
      {screeningComplete &&
      <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Top Ranked Candidates</h3>
            <Button variant="outline" size="sm">
              Export Results
            </Button>
          </div>

          {displayCandidates?.map((candidate, index) =>
        <div key={candidate?.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex flex-col lg:flex-row gap-6">
                {/* Candidate Info */}
                <div className="flex-1">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="relative">
                      <img
                    src={candidate?.avatar}
                    alt={candidate?.avatarAlt}
                    className="w-16 h-16 rounded-full object-cover border-2 border-gray-200" />

                      <div className="absolute -top-1 -right-1 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                        {index + 1}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-lg font-semibold text-gray-900">{candidate?.name}</h4>
                        <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getStatusBadge(candidate?.status)}`}>
                          {candidate?.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{candidate?.position}</p>
                      <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                        <span>{candidate?.email}</span>
                        <span>•</span>
                        <span>{candidate?.phone}</span>
                        <span>•</span>
                        <span>Applied: {candidate?.appliedDate}</span>
                      </div>
                    </div>
                  </div>

                  {/* Score Breakdown */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-600 mb-1">Skills Match</p>
                      <p className={`text-lg font-bold ${getScoreColor(candidate?.scores?.skillsMatch)}`}>
                        {candidate?.scores?.skillsMatch}%
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-600 mb-1">Experience</p>
                      <p className={`text-lg font-bold ${getScoreColor(candidate?.scores?.experience)}`}>
                        {candidate?.scores?.experience}%
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-600 mb-1">Education</p>
                      <p className={`text-lg font-bold ${getScoreColor(candidate?.scores?.education)}`}>
                        {candidate?.scores?.education}%
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-600 mb-1">Projects</p>
                      <p className={`text-lg font-bold ${getScoreColor(candidate?.scores?.projects)}`}>
                        {candidate?.scores?.projects}%
                      </p>
                    </div>
                  </div>

                  {/* Key Highlights */}
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Key Highlights:</p>
                    <ul className="space-y-1">
                      {candidate?.keyHighlights?.map((highlight, idx) =>
                  <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          {highlight}
                        </li>
                  )}
                    </ul>
                  </div>

                  {/* Skills */}
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Skills:</p>
                    <div className="flex flex-wrap gap-2">
                      {candidate?.skills?.map((skill, idx) =>
                  <span key={idx} className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full border border-blue-200">
                          {skill}
                        </span>
                  )}
                    </div>
                  </div>
                </div>

                {/* Overall Score & Actions */}
                <div className="lg:w-48 flex flex-col items-center justify-between border-t lg:border-t-0 lg:border-l border-gray-200 pt-4 lg:pt-0 lg:pl-6">
                  <div className="text-center mb-4">
                    <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full border-4 ${getScoreBg(candidate?.overallScore)} border-gray-200`}>
                      <div>
                        <p className={`text-3xl font-bold ${getScoreColor(candidate?.overallScore)}`}>
                          {candidate?.overallScore}
                        </p>
                        <p className="text-xs text-gray-600">Overall</p>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center justify-center gap-1 text-sm">
                      <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                      <span className="text-gray-700 font-medium">Top Candidate</span>
                    </div>
                  </div>

                  <div className="w-full space-y-2">
                    <Button className="w-full" size="sm">
                      <Eye className="w-4 h-4 mr-2" />
                      View Profile
                    </Button>
                    <Button variant="outline" className="w-full" size="sm">
                      <Download className="w-4 h-4 mr-2" />
                      Resume
                    </Button>
                  </div>
                </div>
              </div>
            </div>
        )}
        </div>
      }
    </div>);


};

export default SmartCandidateScreening;